// Real-time page management for the editor
export class EditorPageManager {
  constructor(quill, config = {}) {
    this.quill = quill;
    this.config = {
      pageHeightMm: config.pageHeight || 297, // A4 height
      marginTopMm: config.marginTop || 20,
      marginBottomMm: config.marginBottom || 20,
      ...config
    };
    
    // IMPORTANT: Match the actual A4 page capacity in the editor
    // A4 page: 297mm height, with 20mm margins = 257mm usable
    // Convert mm to pixels: 257mm * 96dpi / 25.4mm/inch = 971px
    const effectiveHeightMm = this.config.pageHeightMm - this.config.marginTopMm - this.config.marginBottomMm;
    
    // Calculate exact pixel height to match preview
    // 257mm (A4 content area) = 971px at 96 DPI
    this.maxContentHeightPx = Math.round((effectiveHeightMm * 96) / 25.4);
    
    console.log('EditorPageManager initialized with max height:', this.maxContentHeightPx, 'px');
    
    // Track current state
    this.isProcessing = false;
    this.lastCheck = 0;
    this.isDeletingPageBreak = false; // Track if we're deleting a page break
    
    // Bind methods
    this.checkAndInsertPageBreaks = this.checkAndInsertPageBreaks.bind(this);
    this.handleTextChange = this.handleTextChange.bind(this);
  }
  
  // Initialize page management
  init() {
    console.log('EditorPageManager: Initializing real-time page management...');
    
    // Listen for ALL text changes - typing, paste, delete, etc.
    this.quill.on('text-change', this.handleTextChange);
    
    // Also listen for editor changes (for programmatic changes)
    this.quill.on('editor-change', (eventType, ...args) => {
      if (eventType === 'text-change') {
        const [delta, oldDelta, source] = args;
        if (!this.isProcessing) {
          this.checkAndInsertPageBreaks();
        }
      }
    });
    
    // Initial check
    setTimeout(() => this.checkAndInsertPageBreaks(), 100);
  }
  
  // Handle text changes
  handleTextChange(delta, oldDelta, source) {
    console.log('Text change detected, source:', source);
    
    if (!this.isProcessing) {
      // Check immediately for all changes
      requestAnimationFrame(() => {
        this.checkAndInsertPageBreaks();
      });
    }
  }
  
  // Check content and insert page breaks where needed
  checkAndInsertPageBreaks() {
    if (this.isProcessing || this.isDeletingPageBreak) {
      return;
    }
    
    try {
      this.isProcessing = true;
      
      // Get the editor content
      const editorRoot = this.quill.root;
      const content = editorRoot.innerHTML;
      
      // Skip if empty
      if (!content || content === '<p><br></p>' || content === '<p></p>') {
        this.notifyPageUpdate();
        this.isProcessing = false;
        return;
      }
      
      console.log('Checking page overflow...');
      
      // First pass: identify all existing page breaks and build page sections
      const children = Array.from(editorRoot.children);
      let pageSections = [];
      let currentSection = { startIndex: 0, elements: [], height: 0 };
      
      for (let i = 0; i < children.length; i++) {
        const element = children[i];
        
        if (this.isPageBreak(element)) {
          // End current section
          if (currentSection.elements.length > 0 || i === 0) {
            currentSection.endIndex = i - 1;
            pageSections.push(currentSection);
          }
          // Start new section after the page break
          currentSection = { startIndex: i + 1, elements: [], height: 0 };
        } else {
          
          // Add element to current section
          // Get element height INCLUDING margins (offsetHeight doesn't include margins)
          const computedStyle = window.getComputedStyle(element);
          const marginTop = parseFloat(computedStyle.marginTop) || 0;
          const marginBottom = parseFloat(computedStyle.marginBottom) || 0;
          
          // Special handling for images - ensure they're loaded before measuring
          let elementHeight = element.offsetHeight + marginTop + marginBottom;
          
          // Check if element contains images
          const images = element.querySelectorAll('img');
          if (images.length > 0) {
            // For images, use their natural height if available, or a minimum height
            images.forEach(img => {
              if (!img.complete || img.naturalHeight === 0) {
                // Image not loaded yet, use a reasonable default height
                // or schedule a re-check after image loads
                elementHeight = Math.max(elementHeight, 200); // Minimum height for unloaded images
                
                // Add load listener to re-check when image loads
                img.addEventListener('load', () => {
                  // Delay to ensure layout has updated
                  setTimeout(() => {
                    if (!this.isProcessing && !this.isDeletingPageBreak) {
                      this.checkAndInsertPageBreaks();
                    }
                  }, 100);
                }, { once: true });
              }
            });
          }
          
          // Ensure minimum height
          elementHeight = elementHeight || 20;
          
          currentSection.elements.push({
            element,
            index: i,
            height: elementHeight
          });
          currentSection.height += elementHeight;
        }
      }
      
      // Don't forget the last section
      if (currentSection.elements.length > 0) {
        currentSection.endIndex = children.length - 1;
        pageSections.push(currentSection);
      }
      
      console.log(`Found ${pageSections.length} page sections`);
      
      // Second pass: check each section for overflow and determine where to insert breaks
      let pageBreaksNeeded = [];
      
      for (let sectionIdx = 0; sectionIdx < pageSections.length; sectionIdx++) {
        const section = pageSections[sectionIdx];
        console.log(`Section ${sectionIdx}: height=${section.height}px, elements=${section.elements.length}`);
        
        if (section.height > this.maxContentHeightPx && section.elements.length > 0) {
          console.log(`Section ${sectionIdx} exceeds max height (${section.height}px > ${this.maxContentHeightPx}px)`);
          
          // Find where to split this section
          let accumulatedHeight = 0;
          let lastSplitIndex = -1;
          
          for (let j = 0; j < section.elements.length; j++) {
            const elem = section.elements[j];
            const newHeight = accumulatedHeight + elem.height;
            
            if (newHeight > this.maxContentHeightPx && accumulatedHeight > 0) {
              // Need to insert a page break before this element
              const insertAfterIndex = elem.index - 1;
              
              // Make sure we're not inserting at the same position as an existing break
              // and that we're not inserting before the section start
              if (insertAfterIndex >= section.startIndex && insertAfterIndex > lastSplitIndex) {
                pageBreaksNeeded.push(insertAfterIndex);
                console.log(`Will insert page break after element ${insertAfterIndex} in section ${sectionIdx}`);
                lastSplitIndex = insertAfterIndex;
                
                // Reset accumulated height for the new "page" within this section
                accumulatedHeight = elem.height;
              } else {
                accumulatedHeight = newHeight;
              }
            } else {
              accumulatedHeight = newHeight;
            }
          }
        }
      }
      
      // Remove duplicates and sort
      pageBreaksNeeded = [...new Set(pageBreaksNeeded)].sort((a, b) => b - a);
      
      // Insert page breaks if needed
      if (pageBreaksNeeded.length > 0) {
        console.log(`Need to insert ${pageBreaksNeeded.length} page breaks`);
        
        // Get current selection to restore later
        const selection = this.quill.getSelection();
        
        // Sort positions in descending order for insertion
        pageBreaksNeeded.sort((a, b) => b - a);
        
        // Insert breaks from end to beginning to maintain indices
        pageBreaksNeeded.forEach(position => {
          // The position is the element index where we need to insert AFTER
          // We need to find the Quill index at the END of this element
          const index = this.getQuillIndexAfterElement(position);
          if (index > 0) {
            console.log(`Inserting page break after element ${position} at Quill index ${index}`);
            
            // Insert the page break
            this.quill.insertEmbed(index, 'pagebreak', true, 'silent');
            
            // Don't add extra newline - let Quill handle spacing naturally
          }
        });
        
        // Restore selection if possible (adjusted for insertions)
        if (selection) {
          // Count how many breaks were inserted before the selection
          let adjustment = 0;
          pageBreaksNeeded.forEach(position => {
            const index = this.getQuillIndexAfterElement(position);
            if (index <= selection.index) {
              adjustment += 1; // +1 for pagebreak only
            }
          });
          this.quill.setSelection(selection.index + adjustment, selection.length, 'silent');
        }
      }
      
      // Always update page count
      this.notifyPageUpdate();
      
    } catch (error) {
      console.error('Error checking page overflow:', error);
    } finally {
      // Reset flag after a small delay
      setTimeout(() => {
        this.isProcessing = false;
      }, 50);
    }
  }
  
  // Check if an element is a page break
  isPageBreak(element) {
    return element.classList.contains('ql-pagebreak') || 
           element.classList.contains('page-break') ||
           element.getAttribute('data-blot') === 'pagebreak' ||
           element.getAttribute('data-page-break') === 'true';
  }
  
  // Get Quill index for a DOM element position (before the element)
  getQuillIndexForElement(elementIndex) {
    const children = Array.from(this.quill.root.children);
    let quillIndex = 0;
    
    for (let i = 0; i < elementIndex && i < children.length; i++) {
      const element = children[i];
      
      if (this.isPageBreak(element)) {
        quillIndex += 1;
      } else {
        // Add text length plus newline
        const text = element.textContent || '';
        quillIndex += text.length + 1;
      }
    }
    
    return quillIndex;
  }
  
  // Get Quill index AFTER a DOM element (where to insert page break)
  getQuillIndexAfterElement(elementIndex) {
    const children = Array.from(this.quill.root.children);
    let quillIndex = 0;
    
    // Calculate index up to and including the target element
    for (let i = 0; i <= elementIndex && i < children.length; i++) {
      const element = children[i];
      
      if (this.isPageBreak(element)) {
        quillIndex += 1;
      } else {
        // Add text length plus newline
        const text = element.textContent || '';
        quillIndex += text.length + 1;
      }
    }
    
    return quillIndex;
  }
  
  // Count total pages based on page breaks
  countPages() {
    const delta = this.quill.getContents();
    let pageCount = 1;
    
    if (delta && delta.ops) {
      for (const op of delta.ops) {
        if (op.insert && op.insert.pagebreak) {
          pageCount++;
        }
      }
    }
    
    return pageCount;
  }
  
  // Get current page based on cursor position
  getCurrentPage() {
    const selection = this.quill.getSelection();
    if (!selection) return 1;
    
    const delta = this.quill.getContents();
    if (!delta || !delta.ops) return 1;
    
    let currentPage = 1;
    let currentIndex = 0;
    
    for (const op of delta.ops) {
      if (currentIndex >= selection.index) break;
      
      if (op.insert && op.insert.pagebreak) {
        currentPage++;
      }
      
      if (op.insert && typeof op.insert === 'string') {
        currentIndex += op.insert.length;
      } else {
        currentIndex += 1;
      }
    }
    
    return currentPage;
  }
  
  // Notify about page updates
  notifyPageUpdate() {
    const totalPages = this.countPages();
    const currentPage = this.getCurrentPage();
    
    console.log(`Pages: ${currentPage}/${totalPages}`);
    
    // Dispatch event for UI update
    this.quill.root.dispatchEvent(new CustomEvent('pages-updated', {
      detail: { currentPage, totalPages }
    }));
  }
  
  // Force refresh
  refresh() {
    this.checkAndInsertPageBreaks();
  }
}